import { useRef, useState } from 'react';
import { uiFormat, uiText } from '../../i18n';
import { platform } from '../../platform';
import { OffButton } from '../../ui/OffButton';
import { Seg } from '../../ui/Seg';
import { shake } from '../../ui/motion';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioFormFoot } from '../studio/StudioFormParts';
import { StudioSheet } from '../studio/StudioSheet';
import { ReelsHero } from './ReelsHero';
import { mmss, plural, type ReelsPolicy, type ReelsTemplate } from './reels-policy';

const HOLD_MS = 450;
function readHintSeen() {
  try {
    return sessionStorage.getItem('steos:reels:hold-seen') === '1';
  } catch {
    return false;
  }
}

/** Каталог «Рилс с вами» (APP-SPEC-5 §8.1): разделы, карточки шаблонов, пакет «N рилсов сразу», «Выбрать несколько»
    и пример на весь экран по удержанию. Каталог и цены — из политики формы; выбор уходит только в опубликованные поля. */
export function ReelsCatalog({
  policy,
  allowed,
  canMulti,
  canPack,
  initialCategory,
  onOpen,
  onOpenMulti,
  onOpenPack,
}: {
  policy: ReelsPolicy;
  /** Шаблоны, которые принимает поле формы (values); пусто — все шаблоны политики */
  allowed: string[];
  canMulti: boolean;
  canPack: boolean;
  initialCategory?: string;
  onOpen(id: string, category: string): void;
  onOpenMulti(ids: string[], category: string): void;
  onOpenPack(category: string): void;
}) {
  const templates = policy.templates.filter(t => !allowed.length || allowed.includes(t.id));
  const categories = policy.categories.length ? policy.categories : [{ id: 'all', title: 'Все' }];
  const [category, setCategory] = useState(initialCategory && categories.some(c => c.id === initialCategory) ? initialCategory : categories[0].id);
  const [selecting, setSelecting] = useState(false);
  const [marked, setMarked] = useState<string[]>([]);
  const [hint, setHint] = useState('');
  const [hold, setHold] = useState<ReelsTemplate | 'pack' | null>(null);
  const [holdSeen, setHoldSeen] = useState(readHintSeen);
  const timer = useRef(0);
  const held = useRef(false);
  const first = categories[0]?.id;
  const shown =
    category === 'all'
      ? templates
      : [
          ...templates.filter(t => t.categoryId === category),
          // «Для вас» — свои шаблоны раздела и хиты всех разделов
          ...(category === first ? templates.filter(t => t.categoryId !== category && t.badge === 'hit') : []),
        ];
  const say = (text: string, element?: HTMLElement | null) => {
    setHint(text);
    shake(element ?? null);
    platform.haptic('light');
  };
  const openHold = (value: ReelsTemplate | 'pack') => {
    held.current = true;
    setHold(value);
    setHoldSeen(true);
    try {
      sessionStorage.setItem('steos:reels:hold-seen', '1');
    } catch {
      /* подсказка просто покажется ещё раз */
    }
  };
  const startHold = (value: ReelsTemplate | 'pack') => {
    held.current = false;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => openHold(value), HOLD_MS);
  };
  const cancelHold = () => clearTimeout(timer.current);
  const menuHold = (event: React.MouseEvent, value: ReelsTemplate | 'pack') => {
    event.preventDefault();
    clearTimeout(timer.current);
    openHold(value);
  };
  const tap = (value: ReelsTemplate | 'pack', element: HTMLElement) => {
    if (held.current) {
      held.current = false;
      return;
    }
    if (value === 'pack') {
      if (selecting) return say(uiFormat('Пакет собирается отдельно: в нём уже {0} рилсов', policy.pack?.count ?? 10), element);
      return onOpenPack(category);
    }
    if (!selecting) return onOpen(value.id, category);
    setHint('');
    setMarked(list => (list.includes(value.id) ? list.filter(id => id !== value.id) : [...list, value.id]));
  };
  const toggleSelect = () => {
    platform.haptic('light');
    if (selecting) {
      setSelecting(false);
      setMarked([]);
      setHint('');
      return;
    }
    setSelecting(true);
    if (policy.multi && Number.isFinite(policy.multi.discountFrom))
      setHint(uiFormat('Отметьте шаблоны: от {0} скидка {1}%', policy.multi.discountFrom, policy.multi.discountPct));
  };
  const next = () => {
    if (marked.length === 1) onOpen(marked[0], category);
    else if (marked.length > 1) onOpenMulti(marked, category);
  };
  const price = (t: ReelsTemplate) => t.price ?? policy.types[t.type]?.price ?? null;
  const pack = canPack && policy.pack && category === first ? policy.pack : null;
  return (
    <section className="rl-cat">
      <div className="rl-top">
        <Seg
          className="rl-cats sm"
          label={uiText('Разделы')}
          items={categories.map(c => ({ key: c.id, label: uiText(c.title) }))}
          value={category}
          onChange={setCategory}
        />
        {canMulti && (
          <button type="button" className={`iconb rl-selb${selecting ? ' on' : ''}`} aria-pressed={selecting} aria-label={uiText('Выбрать несколько')} onClick={toggleSelect}>
            <StudioIcon kind="check" />
          </button>
        )}
      </div>
      {hint && (
        <p className="rl-tip" role="status">
          {uiText(hint)}
        </p>
      )}
      <div className={`rl-cards${selecting ? ' sm' : ''}`}>
        {pack && (
          <button type="button" className={`rl-card rl-pack${selecting ? ' off' : ''}`} onPointerDown={() => startHold('pack')} onPointerUp={cancelHold} onPointerLeave={cancelHold} onContextMenu={event => menuHold(event, 'pack')} onClick={event => tap('pack', event.currentTarget)}>
            <span className="rl-cv">
              {templates.slice(0, 3).map(t => t.frames[0] && <img key={t.id} src={t.frames[0]} alt="" draggable={false} />)}
            </span>
            {pack.badge && <span className="rl-hb">{uiText(pack.badge === 'hit' ? 'Хит' : 'Новое')}</span>}
            <b>{uiText(pack.title)}</b>
            <span>{uiFormat('{0} разных сюжетов с вами за один заказ', pack.count)}</span>
            {pack.price !== null && (
              <span className="rl-mt">
                <span className="mono">{pack.price} C</span>
                {pack.comparePrice !== null && <> {uiFormat('вместо {0} C', pack.comparePrice)}</>}
              </span>
            )}
          </button>
        )}
        {shown.map(t => {
          const order = marked.indexOf(t.id);
          return (
            <button
              key={t.id}
              type="button"
              className={`rl-card${order >= 0 ? ' on' : ''}`}
              aria-pressed={selecting ? order >= 0 : undefined}
              data-rl-card={t.id}
              onPointerDown={() => startHold(t)}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onContextMenu={event => menuHold(event, t)}
              onClick={event => tap(t, event.currentTarget)}
            >
              <span className="rl-cv">{t.frames[0] ? <img src={t.frames[0]} alt="" draggable={false} /> : <StudioIcon kind="film" />}</span>
              {t.badge && <span className="rl-hb">{uiText(t.badge === 'hit' ? 'Хит' : 'Новое')}</span>}
              {selecting && <span className="ck">{order >= 0 ? order + 1 : ''}</span>}
              <b>{t.title}</b>
              {t.plot && <span>{t.plot}</span>}
            </button>
          );
        })}
      </div>
      {!shown.length && !pack && <p role="status">{uiText('В этом разделе пока нет шаблонов')}</p>}
      {!holdSeen && !selecting && (
        <p className="rl-hh hint" role="note">
          {uiText('Удерживайте карточку, чтобы посмотреть пример')}
        </p>
      )}
      {selecting && (
        <StudioFormFoot>
          <OffButton
            className="fin"
            label={marked.length ? uiFormat('Дальше · {0} {1}', marked.length, plural(marked.length, 'шаблон', 'шаблона', 'шаблонов')) : uiText('Дальше')}
            miss={marked.length ? undefined : uiText('Отметьте шаблоны')}
            onClick={() => marked.length && next()}
          />
        </StudioFormFoot>
      )}
      {hold && (
        <StudioSheet title={hold === 'pack' ? (policy.pack?.title ?? '') : hold.title} onClose={() => setHold(null)}>
          {hold === 'pack' ? (
            <>
              <ReelsHero frames={templates.slice(0, 5).flatMap(t => (t.frames[0] ? [{ url: t.frames[0], label: t.title }] : []))} type="" durationS={policy.pack?.durationS ?? null} badge={policy.pack?.badge ?? null} deck />
              <h2>{uiText(policy.pack?.title ?? '')}</h2>
              <p className="rl-chips">
                {[
                  uiFormat('{0} рилсов', policy.pack?.count ?? 10),
                  policy.pack?.durationS ? uiFormat('по {0}', mmss(policy.pack.durationS)) : '',
                  uiText('Вы в кадре'),
                  policy.pack?.price !== null && policy.pack?.price !== undefined ? `${policy.pack.price} C` : '',
                ]
                  .filter(Boolean)
                  .map(chip => (
                    <span key={chip} className="chip">
                      {chip}
                    </span>
                  ))}
              </p>
            </>
          ) : (
            <>
              <ReelsHero frames={hold.frames.map(url => ({ url }))} type={hold.type} durationS={hold.durationS} badge={hold.badge} />
              <h2>{hold.title}</h2>
              {hold.plot && <p>{hold.plot}</p>}
              <p className="rl-chips">
                {[
                  policy.needs[hold.need]?.label ?? '',
                  hold.durationS !== null ? mmss(hold.durationS) : '',
                  [policy.types[hold.type]?.title, price(hold) !== null ? `${price(hold)} C` : ''].filter(Boolean).join(' · '),
                  policy.variants ? uiFormat('{0} варианта', policy.variants) : '',
                ]
                  .filter(Boolean)
                  .map(chip => (
                    <span key={chip} className="chip">
                      {uiText(chip)}
                    </span>
                  ))}
              </p>
            </>
          )}
          <p className="hint">{uiText('Музыка и монтаж уже в шаблоне')}</p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              const value = hold;
              setHold(null);
              held.current = false;
              if (value === 'pack') {
                if (!selecting) onOpenPack(category);
              } else if (selecting) setMarked(list => (list.includes(value.id) ? list.filter(id => id !== value.id) : [...list, value.id]));
              else onOpen(value.id, category);
            }}
          >
            {uiText(hold !== 'pack' && selecting ? (marked.includes(hold.id) ? 'Снять отметку' : 'Отметить') : 'Открыть шаблон')}
          </button>
        </StudioSheet>
      )}
    </section>
  );
}
